import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useNavigate } from "react-router-dom";

const COLORS = {
  open: "#a8dadc",
  in_progress: "#457b9d",
  awaiting_approval: "#F79009",
  awaiting_parts: "#e63946",
  completed: "#12B76A",
};

/**
 * WorkshopLoadDonut — Chart 1.
 *
 * Segments = current active job cards.  Delivered / cancelled excluded.
 * Centre shows the total.  Legend is compact and sits beside the ring.
 */
export function WorkshopLoadDonut({ segments = [], total = 0 }) {
  const data = useMemo(
    () =>
      (segments || [])
        .filter((s) => s.count > 0)
        .map((s) => ({ ...s, name: s.label, value: s.count })),
    [segments]
  );
  const nav = useNavigate();

  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-5" data-testid="chart-workshop-load">
      <header className="mb-2">
        <h3 className="text-[15px] font-semibold text-[#1d3557]">Current Workshop Load</h3>
        <p className="text-[11.5px] text-[#667085] mt-0.5">
          Where every active vehicle currently sits in the workflow.
        </p>
      </header>

      {total === 0 ? (
        <div className="text-center py-12">
          <div className="text-[13px] font-semibold text-[#1d3557]">No active jobs</div>
          <p className="text-[11.5px] text-[#667085] mt-1">
            Job cards will appear here as vehicles come in.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.2fr] gap-4 items-center">
          <div className="relative h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  innerRadius={62}
                  outerRadius={90}
                  paddingAngle={2}
                  onClick={(d) => d?.key && nav(`/job-cards?status=${d.key}`)}
                  stroke="none"
                >
                  {data.map((s) => (
                    <Cell
                      key={s.key}
                      fill={COLORS[s.key] || "#457b9d"}
                      style={{ cursor: "pointer" }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  cursor={{ fill: "transparent" }}
                  contentStyle={{
                    background: "#1d3557",
                    border: "none",
                    borderRadius: 6,
                    color: "white",
                    fontSize: 12,
                    padding: "6px 10px",
                  }}
                  itemStyle={{ color: "white" }}
                  formatter={(v, _n, p) => [`${v} job${v === 1 ? "" : "s"}`, p?.payload?.label]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <div className="text-[26px] font-bold text-[#1d3557] font-mono tabular-nums leading-none">
                  {total}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#667085] mt-0.5">
                  active
                </div>
              </div>
            </div>
          </div>

          <ul className="space-y-1.5" data-testid="workshop-load-legend">
            {segments.map((s) => {
              const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    disabled={s.count === 0}
                    onClick={() => nav(`/job-cards?status=${s.key}`)}
                    data-testid={`workshop-load-seg-${s.key}`}
                    className="w-full flex items-center gap-2.5 py-1.5 px-2 rounded hover:bg-[#F7F9FC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ background: COLORS[s.key] || "#457b9d" }}
                    />
                    <span className="text-[12.5px] font-medium text-[#1d3557] flex-1 text-left truncate">
                      {s.label}
                    </span>
                    <span className="text-[12px] font-mono tabular-nums text-[#1d3557]">
                      {s.count}
                    </span>
                    <span className="text-[10.5px] text-[#667085] w-10 text-right">
                      {pct}%
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
